import { appURLs } from "@/app/app-urls.mjs";
import type { GameDefinition } from "@/app/game-definition.mjs";
import type { PlatformType } from "@/game-fortnite/constants/platforms.mjs";
import { categories } from "@/game-fortnite/constants/weaponCategories.mjs";
import { GAME_SYMBOL_FORTNITE } from "@/game-fortnite/definition-symbol.mjs";
import type { ConsoleMatch } from "@/game-fortnite/models/console-matches.mjs";
import type { ItemShop } from "@/game-fortnite/models/item-shop.mjs";
import type { Items, WeaponItems } from "@/game-fortnite/models/items.mjs";
import type { Leaderboard } from "@/game-fortnite/models/leaderboard.mjs";
import type { Locker } from "@/game-fortnite/models/locker.mjs";
import type { MatchQuery } from "@/game-fortnite/models/match-query.mjs";
import type {
  Matchlist,
  PlayerMatches,
} from "@/game-fortnite/models/matchlist.mjs";
import type { MapData } from "@/game-fortnite/models/model-wiki.mjs";
import type { PlayerRanks } from "@/game-fortnite/models/player-ranks.mjs";
import type { PlayerSummary } from "@/game-fortnite/models/player-summaries.mjs";
import type { PlayerWeaponStats } from "@/game-fortnite/models/player-weapon-stats.mjs";
import type { PowerRankingLeaderboard } from "@/game-fortnite/models/power-ranking-leaderboard.mjs";
import type { Player as ProfileType } from "@/game-fortnite/models/profile.mjs";
import type { Reactions } from "@/game-fortnite/models/reactions.mjs";
import type { FortniteSeasons } from "@/game-fortnite/models/season.mjs";
import type { TeamsLeaderboard } from "@/game-fortnite/models/teams-leaderboard.mjs";
import type { WeaponStatsMap } from "@/game-fortnite/models/weapon-statistics.mjs";
import routes from "@/game-fortnite/routes.mjs";
import IconFortnite from "@/inline-assets/fn.svg";
import GameIconBoxFortnite from "@/inline-assets/game-icon-box-fn.svg";
import FriendGameIconBoxFN from "@/inline-assets/game-icon-friend-box-fn.svg";

export { GAME_SYMBOL_FORTNITE };

const definition = {
  gameSymbol: GAME_SYMBOL_FORTNITE,
  icon: `${appURLs.CDN_PLAIN}/blitz/ui/images/icons/games/small/FN-small.svg`,
  iconShape: IconFortnite,
  iconBox: GameIconBoxFortnite,
  iconBadge: FriendGameIconBoxFN,
  imageHero: `${appURLs.CDN_PLAIN}/blitz/games/assest/hero-logo/hero-logo-fn.png`,
  imageBanner: `${appURLs.CDN}/blitz/library/banner-fortnite.png`,
  imageKeyart: `${appURLs.CDN}/blitz/ui/img/game-cover/fn-min.webp`,
  imageWordmark: `${appURLs.CDN}/blitz/ui/img/gamecover/name-fn.webp`,
  isActive: true,
  fullName: ["common:games.fortnite.long", "Fortnite"],
  friendlyName: ["common:games.fortnite.long", "Fortnite"],
  preferFriendlyName: false as const,
  shortName: "fortnite" as const,
  backendName: "FORTNITE" as const,
  gameColor: "var(--blue)",

  navLinks: [
    {
      href: `/fortnite/database`,
      text: ["common:navigation.database", "Database"],
      menu: [
        [
          {
            category: ["fortnite:weapons", "Weapons"],
            items: [
              {
                text: ["fortnite:database.weapons.active", "Active"],
                href: "/fortnite/database/weapons/active",
              },
              {
                text: ["fortnite:database.weapons.vaulted", "Vaulted"],
                href: "/fortnite/database/weapons/vaulted",
              },
              ...categories.map((category) => {
                return {
                  text: category.t,
                  href: `/fortnite/database/weapons/vaulted?category=${category.key}`,
                };
              }),
            ],
          },
        ],
        [
          {
            category: ["fortnite:items", "Items"],
            items: [
              {
                text: ["fortnite:resources", "Resources"],
                href: "/fortnite/database/resources",
              },
              {
                text: ["fortnite:utilities", "Utilities"],
                href: "/fortnite/database/utilities",
              },
              {
                text: ["fortnite:consumables", "Consumables"],
                href: "/fortnite/database/consumables",
              },
              {
                text: ["fortnite:vehicles", "Vehicles"],
                href: "/fortnite/database/vehicles",
              },
            ],
          },
        ],
      ],
    },
    {
      href: "/fortnite/map",
      text: ["fortnite:map", "Map"],
    },
    {
      href: `/fortnite/stats`,
      text: ["common:navigation.statistics", "Statistics"],
      menu: [
        [
          {
            items: [
              {
                text: ["common:navigation.weapons", "Weapons"],
                href: `/fortnite/stats/weapons`,
              },
            ],
          },
        ],
      ],
    },
    {
      href: `/fortnite/shop`,
      text: ["common:navigation.shops", "Shop"],
    },
    {
      href: `/fortnite/leaderboard`,
      text: ["common:navigation.leaderboards", "Leaderboards"],
    },
    {
      href: `/fortnite/overlays`,
      text: ["common:navigation.overlays", "Overlays"],
    },
  ],
  settings: () => import("@/game-fortnite/Settings.jsx"),
  IPC: () => import("@/game-fortnite/init.mjs"),
  routes,
  meta: {
    game: {
      title: [
        "home:meta.home.fortnite.title",
        "Fortnite Tracker - The Best Stats, Item Shop, Leaderboards, and Insights",
      ],
      description: [
        "home:meta.home.fortnite.description",
        "Use Blitz.gg's Fortnite Tracker to track your stats, monitor your performance, and win more games. Download our desktop app for the best tracking tools and premium real-time overlays!",
      ],
    },
    bg: `${appURLs.CDN}/blitz/ui/img/game-bg/fortnite-home-bg-centered.webp`,
    players: "14M+",
    matches: "8M+",
    guides: [
      () => import("@/game-fortnite/components/HomeShopTile.jsx"),
      () => import("@/game-fortnite/components/HomeStatisticsTile.jsx"),
      // () => import("@/game-fortnite/components/HomeWeaponsDatabase.jsx"),
    ],
    features: [
      {
        title: ["fortnite:features.profile.title", "Profile Page"],
        description: [
          "fortnite:features.profile.description",
          "Track valuable metrics over time to measure your gaming performance",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/fortnite/profile.webp`,
      },
      {
        title: [
          "fortnite:features.dynamic-stats-overlay.title",
          "Dynamic Stats Overlay",
        ],
        description: [
          "fortnite:features.dynamic-stats-overlay.description",
          "Track valuable metrics over time to measure your gaming performance",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/fortnite/overlay-hero.webp`,
      },
      {
        title: ["fortnite:features.weapon-stats.title", "Weapon Statistics"],
        description: [
          "fortnite:features.weapon-stats.description",
          "Check out the most popular Weapons played",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/fortnite/weapon-stats.webp`,
      },
      {
        title: ["fortnite:features.leaderboard.title", "Leaderboard"],
        description: [
          "fortnite:features.leaderboard.description",
          "See how you stack up against the best players from around the world",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/fortnite/leaderboard.webp`,
      },
      {
        title: ["fortnite:features.postMatch.title", "Post Match"],
        description: [
          "fortnite:features.postMatch.description",
          "Review your key battle statistics as soon as your game ends",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/fortnite/postmatch.webp`,
      },
      {
        title: ["common:liveGame", "Live Game"],
        description: [
          "fortnite:features.liveGame.description",
          "Track key combat metrics in real time as you play",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/fortnite/live-game.webp`,
      },
      {
        title: ["common:navigation.database", "Database"],
        description: [
          "fortnite:features.database.description",
          "View in depth stats and info on all items and locations in the game",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/fortnite/database.webp`,
      },
      {
        title: ["fortnite:features.itemShop.title", "Item Shop"],
        description: [
          "fortnite:features.itemShop.description",
          "Check out all the current skins available for purchase",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/fortnite/item-shop.webp`,
      },
    ],
    articles: [],
    homeData: () => import("@/game-fortnite/fetches/home-data.mjs"),
  },
  homepageFeatures: ["blitz-search", "blitz-guides", "blitz-game-features"],
} satisfies GameDefinition<typeof GAME_SYMBOL_FORTNITE>;

declare module "@/__main__/app-state.mjs" {
  interface AppState {
    fortnite: {
      profiles: Record<string, ProfileType>;
      profileSummaries: {
        [profileId: string]: {
          [season: string]: Record<string, PlayerSummary>;
        };
      };
      playerRanks: {
        [profileId: string]: {
          [season: string]: PlayerRanks;
        };
      };
      playerMatches: {
        [profileId: string]: {
          [season: string]: Record<string, PlayerMatches>;
        };
      };
      playerWeaponStats: {
        [profileId: string]: {
          [season: string]: Record<string, PlayerWeaponStats>;
        };
      };
      matches: { [gameId: string]: MatchQuery };
      matchlists: {
        [profileId: string]: Record<string, Matchlist>;
      };
      consoleMatches: {
        [profileId: string]: {
          [season: string]: Record<PlatformType, ConsoleMatch[]>;
        };
      };
      stats: {
        weapons: Record<string, WeaponStatsMap>;
        maps: object;
        ranks: object;
        weaponHistoricStats: object;
      };
      liveGame: object | null;
      seasons: FortniteSeasons;
      leaderboard: {
        general: Leaderboard;
        ranked: Leaderboard;
        "power-ranking": PowerRankingLeaderboard;
        teams: TeamsLeaderboard;
      };
      rt: object;
      wiki: { i18n: Record<string, Record<string, string>> } & Record<
        string,
        Record<string, MapData>
      >;
      itemShopV2: ItemShop;
      reactions: Reactions;
      userReactions: Record<string, Reactions>;
      locker: Record<string, Locker>;
      items: Items;
      active: WeaponItems;
      vaulted: WeaponItems;
      weaponItems: Items;
    };
  }
}

export default definition;
